/**
 * useActivityTracker
 *
 * Fires a `logPageVisit` mutation every time the user navigates to a new route.
 * Debounced to 1.5 s so rapid back/forward clicks don't spam the DB.
 * Only runs when the user is authenticated.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/utils/trpc";

// ─── Route → Module / Label map ──────────────────────────────────────────────

interface RouteInfo {
  module: string;
  label: string;
}

function resolveRoute(path: string): RouteInfo {
  const p = path.split("?")[0]; // strip query string

  if (p === "/" || p === "") return { module: "Dashboard", label: "Dashboard" };
  if (p.startsWith("/requests/new")) return { module: "Requests", label: "New Request" };
  if (p.match(/^\/requests\/\d+\/edit/)) return { module: "Requests", label: "Edit Request" };
  if (p.match(/^\/requests\/\d+/)) return { module: "Requests", label: "Request Detail" };
  if (p === "/requests") return { module: "Requests", label: "All Requests" };
  if (p === "/approvals") return { module: "Approvals", label: "My Approvals" };
  if (p === "/approvals/history") return { module: "Approvals", label: "Approval History" };
  if (p === "/sites") return { module: "Facilities", label: "Sites" };
  if (p.match(/^\/sites\/\d+/)) return { module: "Facilities", label: "Site Detail" };
  if (p === "/zones") return { module: "Facilities", label: "Zones" };
  if (p === "/areas") return { module: "Facilities", label: "Areas" };
  if (p === "/global-overwatch") return { module: "Security", label: "Global Overwatch" };
  if (p === "/alerts") return { module: "Security", label: "Security Alerts" };
  if (p === "/checkpoint") return { module: "Security", label: "Checkpoint" };
  if (p === "/checkpoint/search") return { module: "Security", label: "Checkpoint Search" };
  if (p === "/checkpoint/security-dashboard") return { module: "Security", label: "Security Dashboard" };
  if (p === "/checkpoint/watchlist") return { module: "Security", label: "Watchlist" };
  if (p === "/checkpoint-incidents") return { module: "Security", label: "Checkpoint Incidents" };
  if (p === "/checkpoint/unregistered-entry") return { module: "Security", label: "Unregistered Entry" };
  if (p === "/checkpoint/fake-pass-report") return { module: "Security", label: "Fake Pass Report" };
  if (p === "/groups") return { module: "Administration", label: "Groups" };
  if (p === "/companies") return { module: "Administration", label: "Companies" };
  if (p.match(/^\/companies\/\d+/)) return { module: "Administration", label: "Company Detail" };
  if (p === "/users") return { module: "Administration", label: "Users" };
  if (p === "/material-types") return { module: "Administration", label: "Material Types" };
  if (p === "/settings") return { module: "Settings", label: "Settings" };
  if (p === "/settings/translations") return { module: "Settings", label: "Translations" };
  if (p === "/settings/request-types") return { module: "Settings", label: "Request Types" };
  if (p === "/integration-hub") return { module: "Integration Hub", label: "Integration Hub" };
  if (p.startsWith("/integration-hub")) return { module: "Integration Hub", label: "Integration Hub" };
  if (p === "/analytics") return { module: "Analytics", label: "Analytics" };
  if (p === "/reports") return { module: "Reports", label: "Reports" };
  if (p === "/knowledge-center") return { module: "Knowledge Center", label: "Knowledge Center" };
  if (p.startsWith("/knowledge-center")) return { module: "Knowledge Center", label: "Knowledge Center" };
  if (p === "/workflows") return { module: "Workflow Builder", label: "Workflows" };
  if (p === "/workflow-builder") return { module: "Workflow Builder", label: "Workflow Builder" };
  if (p === "/delegations") return { module: "Delegations", label: "Delegations" };
  if (p === "/mcm") return { module: "MCM", label: "Card Management" };
  if (p === "/mcm/cards") return { module: "MCM", label: "Cards" };
  if (p === "/mcm/request/new") return { module: "MCM", label: "New Card Request" };
  if (p.match(/^\/mcm\/requests\/\d+/)) return { module: "MCM", label: "Card Request Detail" };
  if (p === "/profile") return { module: "Profile", label: "My Profile" };
  if (p === "/security-alert-config") return { module: "Security", label: "Alert Configuration" };
  if (p === "/checkpoint-integrations") return { module: "Security", label: "Checkpoint Integrations" };

  // Fallback: humanise the first path segment
  const segment = p.split("/").filter(Boolean)[0] ?? "Unknown";
  const label = segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return { module: label, label };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useActivityTracker(isAuthenticated: boolean) {
  const [location] = useLocation();
  const logMutation = trpc.users.logPageVisit.useMutation();
  const lastTracked = useRef<string>("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (location === lastTracked.current) return;

    // Skip login / public pages
    const skip = ["/login", "/reset-password", "/access-denied", "/404", "/api-docs"];
    if (skip.some((s) => location.startsWith(s))) return;

    // Debounce
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const { module, label } = resolveRoute(location);
      lastTracked.current = location;
      logMutation.mutate({ path: location, module, label });
    }, 1500);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [location, isAuthenticated]);
}
